import type { ErrorRequestHandler, RequestHandler } from "express";
import type { ExpressErrorUtility } from "./utility";

export interface BasicMiddlewareOptions {
    projectName: string;
    apiKey: string;
}

type BaseHandlerContext<TErrorUtility extends ExpressErrorUtility> = {
    instance: TErrorUtility;
};

export type ExpressMiddlewareSetup<
    TMiddlewareOptions extends BasicMiddlewareOptions
> = (opts: TMiddlewareOptions) => {
        initialize: RequestHandler;
        onError: ErrorRequestHandler;
    }

/*
    The setupTryCatchFactory function allows the construction of a setupTryCatch
    with a consistent interface for both testing and normal use.
    Here, we use a custom instance of the ExpressErrorUtility that exposes a mapError method.
    Subsequently, we insert the mapped error into the mock server response, enabling us to test
    the mapping with real parameters
*/
export const setupTryCatchFactory = <
    TErrorUtility extends ExpressErrorUtility,
    THandlerContext extends BaseHandlerContext<TErrorUtility>,
    TMiddlewareOptions extends BasicMiddlewareOptions = BasicMiddlewareOptions
>(factoryOptions: {
    createContext: (options: TMiddlewareOptions) => THandlerContext;
    handlers: {
        initialize: (context: THandlerContext) => RequestHandler;
        onError: (context: THandlerContext) => ErrorRequestHandler;
    };
}): ExpressMiddlewareSetup<TMiddlewareOptions> => {
    return (options: TMiddlewareOptions) => {
        const context = factoryOptions.createContext(options);

        return {
            initialize: factoryOptions.handlers.initialize(context),
            onError: factoryOptions.handlers.onError(context),
        };
    };
};
